import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { s } from './clientesEstilos';
import { comprimirImagen } from './clientesHelpers';

/**
 * Las fotos de la instalación de un cliente: se suben y se borran aquí, en la ficha.
 *
 * Antes esto vivía en la pantalla de Detalle, que es la que se abre para MIRAR. Quien solo
 * quería ver cómo quedó la instalación tenía a un clic el botón de borrar, y quien estaba
 * creando o corrigiendo un cliente no podía adjuntarlas sin guardar, salir y volver a
 * entrar por otro camino.
 *
 * Al CREAR un cliente todavía no hay a quién colgarle las fotos, así que se guardan en
 * memoria y se suben solas en cuanto el cliente existe. Quien las eligió no tiene por qué
 * saber que el sistema necesita un id primero.
 */
export default function FotosCliente({ clienteId, pendientes, alCambiarPendientes }) {
  const [fotos, setFotos] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [mirando, setMirando] = useState(null);
  const [msg, setMsg] = useState('');

  const cargar = useCallback(async () => {
    if (!clienteId) { setFotos([]); return; }
    try {
      const { data } = await api.get(`/api/fotos/${clienteId}/fotos`);
      setFotos(data || []);
    } catch (_) { setFotos([]); }
  }, [clienteId]);

  useEffect(() => { cargar(); }, [cargar]);

  const elegir = async (e) => {
    const archivo = e.target.files[0];
    e.target.value = '';                       // para poder repetir la misma foto
    if (!archivo) return;
    if (archivo.size > 15 * 1024 * 1024) { setMsg('La foto no debe pasar de 15 MB'); return; }
    setSubiendo(true); setMsg('');
    try {
      const base64 = await comprimirImagen(archivo);
      if (clienteId) {
        await api.post(`/api/fotos/${clienteId}/fotos`,
          { foto_base64: base64, mime_type: 'image/jpeg', nombre: archivo.name });
        cargar();
      } else {
        // Cliente nuevo: se espera a que exista. Las sube el formulario al guardar.
        alCambiarPendientes([...(pendientes || []),
          { nombre: archivo.name, foto_base64: base64, mime_type: 'image/jpeg' }]);
      }
    } catch (_) { setMsg('No se pudo subir la foto'); }
    finally { setSubiendo(false); }
  };

  const borrar = async (foto) => {
    if (!window.confirm(`¿Eliminar la foto «${foto.nombre}»?`)) return;
    try { await api.delete(`/api/fotos/fotos/${foto.id}`); cargar(); }
    catch (_) { setMsg('No se pudo eliminar'); }
  };

  const quitarPendiente = (i) =>
    alCambiarPendientes((pendientes || []).filter((_, j) => j !== i));

  const abrir = async (foto) => {
    try {
      const { data } = await api.get(`/api/fotos/fotos/${foto.id}`);
      setMirando(data);
    } catch (_) { setMsg('No se pudo abrir la foto'); }
  };

  const total = fotos.length + (pendientes || []).length;

  return (
    <div style={{ ...s.campo, gridColumn:'1/-1', background:'rgba(99,102,241,0.05)',
                  border:'1px solid rgba(99,102,241,0.18)', borderRadius:'10px', padding:'14px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
        <label style={{ ...s.label, color:'#A5B4FC', fontWeight:'600' }}>
          📷 Fotos de la instalación{total ? ` (${total})` : ''}
        </label>
        <label style={est.subir}>
          {subiendo ? '⟳ Subiendo…' : '+ Agregar foto'}
          <input type="file" accept="image/*" style={{ display:'none' }}
            onChange={elegir} disabled={subiendo} />
        </label>
      </div>

      {msg && <div style={est.error} onClick={() => setMsg('')}>{msg}</div>}

      {total === 0 ? (
        <div style={est.vacio}>Sin fotos todavía.</div>
      ) : (
        <div style={est.rejilla}>
          {/* Las que ya están guardadas */}
          {fotos.map(foto => (
            <div key={foto.id} style={est.tarjeta}>
              <button type="button" style={est.abrir} onClick={() => abrir(foto)}>
                <div style={est.nombre}>{foto.nombre}</div>
                <div style={est.fecha}>
                  {new Date(foto.creado_en).toLocaleDateString('es-CO')}
                  {foto.subido_por ? ` · ${foto.subido_por}` : ''}
                </div>
              </button>
              <button type="button" style={est.quitar} title="Eliminar"
                onClick={() => borrar(foto)}>✕</button>
            </div>
          ))}
          {/* Y las que están esperando a que el cliente exista */}
          {(pendientes || []).map((p, i) => (
            <div key={`p${i}`} style={{ ...est.tarjeta, borderStyle:'dashed' }}>
              <div style={{ flex:1, padding:'8px 10px', textAlign:'left' }}>
                <div style={est.nombre}>{p.nombre}</div>
                <div style={{ ...est.fecha, color:'#FDE047' }}>se sube al guardar</div>
              </div>
              <button type="button" style={est.quitar} title="Quitar"
                onClick={() => quitarPendiente(i)}>✕</button>
            </div>
          ))}
        </div>
      )}

      {mirando && (
        <div style={est.visor} onClick={() => setMirando(null)}>
          <img src={`data:${mirando.mime_type};base64,${mirando.foto_base64}`}
            alt={mirando.nombre} style={est.imagen} />
          <button type="button" style={est.cerrar} onClick={() => setMirando(null)}>✕ Cerrar</button>
        </div>
      )}
    </div>
  );
}

const est = {
  subir: { background:'rgba(99,102,241,0.18)', border:'1px solid rgba(99,102,241,0.4)',
           borderRadius:'8px', padding:'7px 13px', color:'#C7D2FE', fontSize:'12.5px',
           cursor:'pointer', whiteSpace:'nowrap' },
  vacio: { fontSize:'12px', color:'rgba(255,255,255,0.4)', marginTop:'8px' },
  rejilla: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(190px, 1fr))',
             gap:'8px', marginTop:'10px' },
  tarjeta: { display:'flex', alignItems:'center', border:'1px solid rgba(255,255,255,0.12)',
             borderRadius:'8px', overflow:'hidden', background:'rgba(255,255,255,0.03)' },
  abrir: { flex:1, background:'none', border:'none', color:'#E2E8F0', cursor:'pointer',
           padding:'8px 10px', textAlign:'left' },
  nombre: { fontSize:'12px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  fecha: { fontSize:'10.5px', color:'rgba(255,255,255,0.4)', marginTop:'2px' },
  quitar: { background:'rgba(239,68,68,0.15)', border:'none', borderLeft:'1px solid rgba(255,255,255,0.1)',
            color:'#FCA5A5', cursor:'pointer', padding:'0 10px', alignSelf:'stretch', fontSize:'13px' },
  error: { background:'rgba(239,68,68,0.14)', border:'1px solid rgba(239,68,68,0.3)',
           borderRadius:'8px', padding:'7px 10px', fontSize:'12px', color:'#FCA5A5',
           marginTop:'8px', cursor:'pointer' },
  visor: { position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', display:'flex',
           alignItems:'center', justifyContent:'center', zIndex:2000, flexDirection:'column', gap:'12px' },
  imagen: { maxWidth:'92vw', maxHeight:'82vh', borderRadius:'10px' },
  cerrar: { background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.25)',
            borderRadius:'8px', padding:'8px 16px', color:'#fff', cursor:'pointer' },
};
